import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
} from '@angular/core';
import { Observable } from 'rxjs';
import {
  ProductionColumn,
  ProductionColumnAttachment,
  ProductionColumnHeader,
  ProductionColumnHeaderRequest,
  ProductionColumnHeaderToPost,
} from 'src/app/interfaces/production-column';
import { DialogService } from 'src/app/services/dialog.service';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SectionTouched } from '../shared/section-touched.class';
import {
  MORE_THAN_ONE_AND_SAME_TYPE_FACILITIES,
  MORE_THAN_THREE_FACILITIES,
  SAME_TYPE_FACILITIES,
} from './handover-detail-production-column-messages';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-handover-detail-production-column',
  templateUrl: './handover-detail-production-column.component.html',
  styleUrls: ['./handover-detail-production-column.component.scss'],
  standalone: false,
})
export class HandoverDetailProductionColumnComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handoverId: number | undefined;
  @Input() wellId: string | undefined;
  @Input() handoverStatusId: number | undefined;
  @Input() readOnlyContent: boolean | undefined;

  isLoading = false;
  productionColumnHeaders: ProductionColumnHeader[] = [];
  productionColumnDetailUnfiltered: ProductionColumn[] = [];
  productionColumnDetailFiltered: ProductionColumn[] = [];
  untouchedProductionColumnDetailFiltered: ProductionColumn[] = [];
  displayFacilities = false;
  confirmFacilities = false;
  atLeastOneConfirmed = false;
  fileLimit = 2;
  buttonFile = false;
  uploadfile = false;
  productionColumnAttachment: any;
  dhoApiUrl = `${environment.urlBackend}/dhoapi`;

  displayedColumns = [
    'assemblyName',
    'component',
    'size',
    'joints',
    'length',
    'mdTop',
    'mdBase',
    'weight',
    'grade',
    'threads',
    'condition',
    'manufacturer',
    // 'statusInstallDate',
    'actions',
  ];

  isDisabled: boolean[] = [];
  displayStickyHeader = false;
  // Y Position from which the sticky header should be displayed.
  // Recalculated after the table content has been loaded.
  stickyHeaderScrollStart = 192;

  constructor(
    private handoverService: HandoverService,
    private notificationService: NotificationService,
    public dialogService: DialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getProductionColumnData();
    this.getProductionColumnAttachments();
  }

  getProductionColumnData(): void {
    if (this.handoverId && this.wellId && this.handoverStatusId) {
      this.isLoading = true;
      this.handoverService
        .getProductionColumnHeaders(
          this.handoverId,
          this.wellId,
          this.handoverStatusId
        )
        .subscribe(
          (res) => {
            this.productionColumnHeaders = res.headers;
            this.confirmFacilities = res.confirmedHeaders;
            if (
              res.headers.length &&
              this.handoverId &&
              this.wellId &&
              this.handoverStatusId
            ) {
              this.stickyHeaderScrollStart += 45 * res.headers.length;
              this.handoverService
                .getProductionColumns(
                  this.handoverId,
                  this.wellId,
                  this.handoverStatusId
                )
                .subscribe(
                  (columns) => {
                    this.productionColumnDetailUnfiltered = columns;
                    this.setProductionColumnDetailInitialStatus();
                    this.isLoading = false;
                  },
                  () => {
                    this.isLoading = false;
                  }
                );
            } else {
              this.isLoading = false;
            }
          },
          () => {
            this.isLoading = false;
          }
        );
    }
  }

  setProductionColumnDetailInitialStatus(): void {
    if (this.productionColumnHeaders.length == 1) {
      this.confirmFacilities = true;
      this.displayFacilities = true;
      // Se confirma automáticamente la única instalación obtenida,
      // de acuerdo a regla de negocio.
      this.productionColumnHeaders[0].confirmed = true;
      if (!this.readOnlyContent && this.handoverId && this.wellId) {
        this.saveProductionColumnHeaders();
      }
    } else if (this.productionColumnHeaders.length > 1) {
      this.displayFacilities = false;
    }
    this.filterFacilitiesDetail();
    this.generateUserWarnings();
    if (this.productionColumnDetailFiltered.length) {
      this.setUntouchedProductionColumnDetail(
        this.productionColumnDetailFiltered
      );
    }
  }

  saveProductionColumnHeaders(): Observable<ProductionColumnHeaderRequest> | void {
    if (this.handoverId && this.wellId) {
      // Filter only the data that need to save.
      const headersToPost = this.productionColumnHeaders.map(
        (value: ProductionColumnHeader) => {
          return {
            handoverId: value.handoverId,
            wellId: value.wellId,
            wellboreId: value.wellboreId,
            assemblyId: value.assemblyId,
            confirmed: value.confirmed || false,
          } as ProductionColumnHeaderToPost;
        }
      );
      return this.handoverService.saveProductionColumnHeaders(
        {
          headers: headersToPost,
          confirmedHeaders: this.confirmFacilities,
        },
        this.handoverId,
        this.wellId
      );
    }
  }

  /**
   * Filtra detalle instalaciones basado en status de
   * cabeceras, respondiendo a eventos.
   * @returns void
   */
  filterFacilitiesDetail(): void {
    this.productionColumnDetailFiltered = [];
    this.atLeastOneConfirmed = false;
    this.displayFacilities = false;
    this.productionColumnHeaders.forEach((header: ProductionColumnHeader) => {
      if (header.confirmed) {
        this.atLeastOneConfirmed = true;
        const filtered = this.productionColumnDetailUnfiltered.filter(
          (column) => {
            return column.assemblyId == header.assemblyId;
          }
        );
        this.productionColumnDetailFiltered = filtered.concat(
          this.productionColumnDetailFiltered
        );
      }
    });
    if (this.productionColumnDetailFiltered.length) {
      this.displayFacilities = true;
    }
  }

  setUntouchedProductionColumnDetail(columns: ProductionColumn[]): void {
    this.untouchedProductionColumnDetailFiltered = columns.map((column) => {
      this.isDisabled.push(true);
      return Object.assign({ isDisabled: true }, column);
    });
  }

  toggleConfirmAllFacilities(event: any): void {
    const checked = event.checked;
    this.confirmFacilities = checked;
    this.isLoading = true;
    const saveSubscription = this.saveProductionColumnHeaders();
    if (saveSubscription) {
      saveSubscription.subscribe(() => {
        this.isLoading = false;
      });
    }
  }

  toggleConfirmFacilities(index: number): void {
    this.productionColumnHeaders[index].confirmed =
      !this.productionColumnHeaders[index].confirmed;
    const saveSubscription = this.saveProductionColumnHeaders();
    if (saveSubscription) {
      saveSubscription.subscribe(() => {
        this.filterFacilitiesDetail();
        this.setUntouchedProductionColumnDetail(
          this.productionColumnDetailFiltered
        );
      });
    }
  }

  generateUserWarnings(): void {
    let moreThanThreeFacilities = false;
    const facilitiesTypes: { [key: string]: number } = {};
    let previouslyConfirmedFacilities = 0;
    if (this.productionColumnHeaders.length > 3) {
      moreThanThreeFacilities = true;
    }
    this.productionColumnHeaders.forEach((val) => {
      if (!facilitiesTypes[val.assemblyName]) {
        facilitiesTypes[val.assemblyName] = 1;
      } else {
        facilitiesTypes[val.assemblyName] += 1;
      }
      if (val.confirmed) {
        previouslyConfirmedFacilities += 1;
      }
    });
    let sameFacilityTypes = false;
    Object.keys(facilitiesTypes).forEach((key) => {
      if (facilitiesTypes[key] > 1) {
        sameFacilityTypes = true;
      }
    });

    if (!this.readOnlyContent && !previouslyConfirmedFacilities) {
      if (moreThanThreeFacilities && !sameFacilityTypes) {
        this.notificationService.notifyManualClose(
          MORE_THAN_THREE_FACILITIES,
          true
        );
      } else if (!moreThanThreeFacilities && sameFacilityTypes) {
        this.notificationService.notifyManualClose(SAME_TYPE_FACILITIES, true);
      } else if (moreThanThreeFacilities && sameFacilityTypes) {
        this.notificationService.notifyManualClose(
          MORE_THAN_ONE_AND_SAME_TYPE_FACILITIES,
          true
        );
      }
    }
  }

  openComments(
    element: ProductionColumn,
    index: number,
    event: any,
    readOnly: boolean | undefined
  ): void {
    const target = new ElementRef(event.currentTarget);
    this.dialogService
      .openCommentDialog(
        'Comentarios',
        this.productionColumnDetailFiltered[index].comments,
        target,
        -200,
        -450,
        readOnly
      )
      .afterClosed()
      .subscribe((comments: string) => {
        this.productionColumnDetailFiltered[index].comments = comments;
        this.enableSaveButton(index);
      });
  }

  enableSaveButton(i: number): void {
    const oSp = this.untouchedProductionColumnDetailFiltered[i];
    const enable =
      this.productionColumnDetailFiltered[i].comments.trim() == '' &&
      oSp.comments == null
        ? false
        : this.productionColumnDetailFiltered[i].comments != oSp.comments;
    this.isDisabled[i] = !enable;
    this.reportPendingChanges();
  }

  save(element: ProductionColumn, index: number): void {
    const dialogRef = this.dialogService.openConfirmDialog(
      'Columna de Producción',
      `¿Desea guardar los cambios realizados en la instalación ${element.component}?`
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (res && this.handoverId && this.wellId) {
        const columns: ProductionColumn[] = [];
        columns.push(element);

        this.handoverService
          .saveProductionColumns(columns, this.handoverId, this.wellId)
          .subscribe((savedFacilities: ProductionColumn[]) => {
            if (savedFacilities) {
              this.notificationService.notify(
                `Los cambios en la instalacón ${element.component} se han guardado con éxito.`,
                true
              );
              this.isDisabled[index] = true;
              this.untouchedProductionColumnDetailFiltered[index].comments =
                element.comments;
            } else {
              this.notificationService.notify(
                `Ocurrió un error al grabar los cambios en la instalacón ${element.component}.`,
                false
              );
            }
            this.reportPendingChanges();
          });
      }
    });
  }

  @HostListener('window:scroll', ['$event'])
  makeHeaderSticky($event: any): void {
    this.displayStickyHeader =
      $event.target.scrollingElement.scrollTop > this.stickyHeaderScrollStart;
  }

  reportPendingChanges(): void {
    let thereAreChanges = false;
    for (
      let index = 0;
      index < this.untouchedProductionColumnDetailFiltered.length;
      index++
    ) {
      thereAreChanges = thereAreChanges || !this.isDisabled[index];
    }
    this.emitPendingChanges(thereAreChanges);
  }

  onFileSelected(event: any): void {
    const file: File = event.target.files[0];
    if (this.handoverId && file) {
      this.isLoading = true;
      const formData = new FormData();
      formData.append('file', file);
      this.handoverService
        .saveColumnProductionAttachment(this.handoverId, formData)
        .subscribe(
          (uploaded: ProductionColumnAttachment[]) => {
            if (uploaded) {
              this.uploadfile = true;
              this.getProductionColumnAttachments();
            }
          },
          (response: HttpErrorResponse) => {
            this.notificationService.notify(response.error.message, false);
          }
        );
      this.isLoading = false;
    }
  }

  getProductionColumnAttachments(): void {
    if (this.handoverId) {
      this.isLoading = true;
      this.handoverService
        .getProductionColumnAttachments(this.handoverId)
        .subscribe((file: ProductionColumnAttachment[]) => {
          if (file) {
            this.productionColumnAttachment = file;
            this.buttonFile = true;
            this.uploadfile = true;
          }
          this.isLoading = false;
        });
    }
  }

  deletProductionColumnAttachment(file: any): void {
    const dialogRef = this.dialogService.openConfirmDialog(
      'Columna de Producción',
      `¿Está seguro de que desea eliminar el archivo?`
    );
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        if (!this.handoverId) {
          return;
        }
        this.isLoading = true;
        this.handoverService
          .deleteProductionColumnAttachment(this.handoverId, file)
          .subscribe((res: boolean) => {
            if (res) {
              delete this.productionColumnAttachment;
              this.getProductionColumnAttachments();
            }
            this.isLoading = false;
          });
      }
    });
  }
}
